import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ContactModal } from '@/components/crm/ContactModal';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ContactLijst } from '@/features/relaties/ContactLijst';
import { filterNaarUrl, filterUitUrl, haalContacten, type LijstContact, type LijstFilter } from '@/features/relaties/data/contacten';
import type { RelatieClient } from '@/features/relaties/data/organisaties';
import { archiveerContact } from '@/features/relaties/data/archiveren';
import { CONTACTEN_SLEUTEL, naRelatieWijziging } from '@/features/relaties/data/sleutels';
import { useRechten } from '@/features/relaties/ui/Onderdelen';

/* /crm/contacts. De lijst zelf staat in src/features/relaties/ContactLijst.tsx; hier de
   gegevens, de filter in de URL, en de dialogen voor nieuw, bewerken en archiveren
   (ADR 0022). */

const client = supabase as unknown as RelatieClient;

export default function CRMContacts() {
  const { magBewerken, magBeheren } = useRechten();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<{ open: boolean; contact: Record<string, any> | null }>({ open: false, contact: null });

  const lijst = useQuery({ queryKey: [...CONTACTEN_SLEUTEL, 'lijst'], queryFn: () => haalContacten(client) });
  const filter = useMemo(() => filterUitUrl(searchParams), [searchParams]);
  const zetFilter = useCallback((f: LijstFilter) => setSearchParams(filterNaarUrl(f), { replace: true }), [setSearchParams]);

  const archiveer = async (c: LijstContact) => {
    try {
      await archiveerContact(client, c.id);
      toast({ title: 'Contact gearchiveerd', description: `"${c.naam}" staat in het Archief.` });
    } catch (e) {
      toast({ title: 'Archiveren mislukt', description: (e as Error)?.message ?? String(e), variant: 'destructive' });
    }
    naRelatieWijziging(qc);
  };

  return (
    <>
      <ContactLijst
        contacten={lijst.data ?? []}
        laden={lijst.isPending}
        fout={lijst.error as Error | null}
        filter={filter}
        onFilter={zetFilter}
        magBewerken={magBewerken}
        magBeheren={magBeheren}
        onNieuw={() => setModal({ open: true, contact: null })}
        onBewerk={(c) => setModal({ open: true, contact: c.bron })}
        onArchiveer={archiveer}
      />
      <ContactModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false, contact: null })}
        contact={modal.contact as any}
        onSave={() => naRelatieWijziging(qc)}
      />
    </>
  );
}
